import {
  StyledHeader,
  Logo,
  Nav,
  Phrase,
  NavRight,
} from "../../styles/components/Header.styled";
import { Container } from "../../styles/components/Containers.styled";
import {
  Button,
  Buttons,
  LoginButton,
  SubscriptionExpiredButton,
} from "../../styles/components/Buttons.styled";
import { useAccountContext } from "../../contexts/Account";
import { useLoginStageContext } from "../../contexts/LoginStageContext";
import LoginFlowPopup from "../../components/modals/LoginFlowPopup";
import { useUserDataContext } from "../../contexts/UserDataContext";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import MyAccountOptions from "./Header/MyAccountOptions";

const MY_ITEMS_ROUTES = ["/myitems", "/success", "/cancel"];

export function Header() {
  const { loggedInUserCognitoIdToken, impersonatedUserId, impersonatedUserEmail, stopImpersonation } = useAccountContext();
  const { setLoginStage } = useLoginStageContext();
  const location = useLocation();
  const isMyItemsRoute = MY_ITEMS_ROUTES.includes(location.pathname.toLowerCase());
  const {
    logoutUser,
    setCheckoutPopupOpen,
    subscriptionExpired,
    subscriptionStatus,
    pendingQueueCount,
    offlineFailureCount,
    setOfflineFailureCount,
    offlineSyncedCount,
    setOfflineSyncedCount,
    syncAvailable,
    refreshFromRemote,
  } = useUserDataContext();


  // Auto-dismiss success toast after 4 seconds
  useEffect(() => {
    if (offlineSyncedCount !== null) {
      const timer = setTimeout(() => setOfflineSyncedCount(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [offlineSyncedCount]);

  // The sync banner below is sticky and sits above the sticky search nav
  // (HeaderNav). Publish its height as a CSS var so the nav's sticky offset
  // can account for it and the two don't overlap when both are stuck.
  const syncBannerRef = useRef<HTMLDivElement>(null);
  const showSyncBanner = syncAvailable && isMyItemsRoute;
  useEffect(() => {
    const el = syncBannerRef.current;
    const root = document.documentElement;
    if (!showSyncBanner || !el) {
      root.style.setProperty("--sync-banner-height", "0px");
      return;
    }
    // Use getBoundingClientRect (not entry.contentRect, which excludes padding)
    // so the offset matches the banner's true rendered height.
    const observer = new ResizeObserver(() => {
      root.style.setProperty("--sync-banner-height", `${el.getBoundingClientRect().height}px`);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.setProperty("--sync-banner-height", "0px");
    };
  }, [showSyncBanner]);

  const handleStopImpersonation = () => {
    if (window.confirm("Stop impersonating and return to your account?")) {
      stopImpersonation();
      window.location.reload();
    }
  };

  return (
    <>
      {/* Persistent impersonation warning — shown on every page, not just My Account,
          so it's never ambiguous whose data is being edited. */}
      {impersonatedUserId && (
        <div style={{
          position: "sticky", top: 0, zIndex: 1001,
          background: "#f59e0b", color: "#000", textAlign: "center",
          padding: "6px 12px", fontSize: "13px", fontWeight: 700,
          display: "flex", alignItems: "center", justifyContent: "center", gap: "12px",
        }}>
          <span>⚠️ Impersonating user: {impersonatedUserEmail ?? impersonatedUserId}</span>
          <button
            onClick={refreshFromRemote}
            style={{
              background: "rgba(0,0,0,0.15)", color: "#000", border: "1px solid rgba(0,0,0,0.4)",
              borderRadius: "4px", padding: "2px 10px", fontSize: "13px",
              cursor: "pointer", fontWeight: 700,
            }}
          >
            Refresh Data
          </button>
          <button
            onClick={handleStopImpersonation}
            style={{
              background: "rgba(0,0,0,0.15)", color: "#000", border: "1px solid rgba(0,0,0,0.4)",
              borderRadius: "4px", padding: "2px 10px", fontSize: "13px",
              cursor: "pointer", fontWeight: 700,
            }}
          >
            Stop
          </button>
        </div>
      )}

      <StyledHeader>
        <Container>
          <Nav>
            <Logo>
              Where's<span>Our?</span>
            </Logo>
            <Phrase>A better way to track your things</Phrase>
            <NavRight>
              <MyAccountOptions/>
            </NavRight>
          </Nav>
          <LoginFlowPopup />
        </Container>
      </StyledHeader>

      {/* Multi-device sync banner — shown when another device/user has written newer data,
          and only while the user is actually on a My Items route */}
      {showSyncBanner && (
        <div ref={syncBannerRef} style={{
          position: "sticky", top: 0, zIndex: 1000,
          background: "#2563eb", color: "#fff", textAlign: "center",
          padding: "6px 12px", fontSize: "13px", fontWeight: 500,
          display: "flex", alignItems: "center", justifyContent: "center", gap: "12px",
        }}>
          <span>Updates available from another device</span>
          <button
            onClick={refreshFromRemote}
            style={{
              background: "rgba(255,255,255,0.2)", color: "#fff", border: "1px solid rgba(255,255,255,0.4)",
              borderRadius: "4px", padding: "2px 10px", fontSize: "13px",
              cursor: "pointer", fontWeight: 600,
            }}
          >
            Refresh
          </button>
        </div>
      )}

      {/* Persistent sync badge — always visible when items are waiting */}
      {pendingQueueCount > 0 && (
        <div style={{
          background: "#f59e0b", color: "#fff", textAlign: "center",
          padding: "6px 12px", fontSize: "13px", fontWeight: 500,
        }}>
          {pendingQueueCount} item{pendingQueueCount !== 1 ? "s" : ""} waiting to sync
        </div>
      )}

      {/* Success toast — auto-dismisses after 4s */}
      {offlineSyncedCount !== null && (
        <div style={{
          background: "#16a34a", color: "#fff", textAlign: "center",
          padding: "6px 12px", fontSize: "13px", fontWeight: 500,
        }}>
          {offlineSyncedCount} item{offlineSyncedCount !== 1 ? "s" : ""} synced
        </div>
      )}

      {/* Failure popup — shown once per 15-minute offline streak */}
      {offlineFailureCount !== null && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 1100,
        }}>
          <div style={{
            background: "#fff", borderRadius: "8px", padding: "24px",
            maxWidth: "340px", width: "90%", textAlign: "center",
          }}>
            <p style={{ margin: "0 0 16px", fontSize: "15px" }}>
              Couldn't save right now. You have{" "}
              <strong>{offlineFailureCount} item{offlineFailureCount !== 1 ? "s" : ""}</strong>{" "}
              waiting to sync — they'll upload automatically when connectivity returns.
            </p>
            <button
              onClick={() => setOfflineFailureCount(null)}
              style={{
                background: "#2563eb", color: "#fff", border: "none",
                borderRadius: "6px", padding: "8px 20px", fontSize: "14px",
                cursor: "pointer",
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
}
